import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll behaviour on navigation:
 *
 *  - PUSH (new page, e.g. Pay & Book → /checkout) → scroll to top.
 *  - POP  (browser back/forward AND our in-app BackRouteButton, which calls
 *         `navigate(-1)`) → restore the scroll position the user was at on
 *         that route last time.
 *  - REPLACE → no-op (e.g. router strips a hash via `navigate(path, { replace }).
 *  - A URL with a hash ("/#section-faqs") → left alone: ScrollToHash scrolls to the section
 *    once it is drawn, and a scroll to the top here would undo it (1NOW-11111).
 *
 * Why we manage scroll ourselves (ONENOW-469): the browser's native
 * `scrollRestoration: 'auto'` clamps to `maxScroll` if the destination page
 * is shorter than its previous state. The fleet detail page fetches data
 * async, so on first paint after a POP it's only ~viewport-tall — the
 * browser then "restores" to Y = pageHeight - viewport, which lands the
 * user at the footer. By switching to `manual` and retrying the scroll as
 * the page grows, we land them back at the position they actually left.
 *
 * Third iteration (ONENOW-469 second reopen): even with manual restore +
 * ResizeObserver retries, we were still landing users near the footer.
 * The cause was the restore *itself* — we were clamping `targetY` to the
 * current `maxY` on every retry and scrolling there. When the destination
 * is a spinner-only early-return (the fleet detail page renders a
 * `min-h-[50vh]` spinner while `selectedCar` rehydrates from context),
 * `maxY` is small but nonzero (header + spinner + footer of the SPA
 * shell), and the clamped scroll lands exactly at the footer of that
 * skeleton. Now we hold the user at Y=0 until the document grows enough
 * to honour the full saved position — only then do we scroll there.
 *
 * Fourth iteration (ONENOW-469 third reopen): the third-iteration fix
 * worked the first time tryRestore succeeded, then `stop()` killed the
 * observer + interval. But the fleet detail page often grows in stages:
 * first paint with cached `selectedCar` is tall enough → we restore →
 * we stop → a subsequent re-render shrinks the document briefly
 * (spinner early-return flashing, lazy banner image not yet loaded,
 * async effect swapping content) → the browser clamps scrollY down to
 * the new (small) maxY → when the page finally settles tall again the
 * user is parked near the now-irrelevant maxY of the *intermediate*
 * state. That looks identical to "lands at footer" to QA. So we keep the
 * interval + ResizeObserver alive long enough to correct a post-restore
 * shrink instead of stopping on the first success.
 *
 * Fifth iteration (1NOW-1087 / ONENOW-499): the fourth iteration's
 * "re-assert targetY on every tick for the full 10 s" turned the restore
 * into a loop that *fought the user*. Once the page had settled, the user
 * trying to scroll UP (away from a targetY below them) was yanked back
 * down on the next 100 ms tick / ResizeObserver callback — the jitter /
 * up-and-down shift reported site-wide on home, listing and detail, on
 * every visit (react-router reports POP on first load too), desktop and
 * mobile. Fix: (1) never arm the watcher when there's nothing to restore
 * (targetY ≤ 0, incl. first load); (2) hand control back permanently the
 * instant the user shows scroll intent (wheel / touchmove / scroll-keys,
 * captured before our own re-assert can race them) or scrolls upward off
 * the position we asserted; (3) only re-assert on a *genuine downward
 * displacement* (the displacement gate the original author flagged as
 * needed) so a user scroll-up is never overridden; (4) stop early once
 * we're parked at targetY and the document height has settled, instead of
 * idling — and re-asserting — for the whole timeout. The ONENOW-469
 * guarantees are preserved: manual scrollRestoration, the
 * `maxY < targetY → scrollTo(0, 0)` park-at-top guard, and re-asserting
 * through staged document growth all stay.
 */

const KEY = "app_scroll_positions";
// Absolute hard stop for restoration attempts. With a `ResizeObserver` we
// re-scroll whenever the document grows, so under normal conditions the
// final scroll happens within a frame or two of the data resolving — the
// long fallback exists for very slow networks (initial 1.5 s budget was
// too short; the page would still be skeleton when retries gave up and
// the user would land at the bottom of the partial content, ≈ the footer
// — ONENOW-469 reopen). Note: we now usually stop far sooner, either when
// the page settles (convergence) or the moment the user scrolls.
const RESTORE_TIMEOUT_MS = 10000;
// Spacing for the setInterval backstop (in case ResizeObserver doesn't
// fire — e.g., content height grows via direct style mutation without
// touching the layout in a way the observer notices).
const RESTORE_TICK_MS = 100;
// Window during which we ignore programmatic scroll events that fire as a
// consequence of navigation (the new page is briefly shorter while data
// loads, the browser clamps scrollY downward, and that clamp fires a scroll
// event that we'd otherwise misattribute to the still-current URL).
const NAV_QUIET_MS = 600;
// Sub-pixel / 1px clamp noise we ignore when comparing scroll positions.
const RESTORE_THRESHOLD_PX = 4;
// Once we're parked at targetY and the document height has held steady for
// this many consecutive ticks, the page has settled — stop the watcher
// instead of keeping it alive (and re-asserting) for the full timeout.
const RESTORE_STABLE_TICKS = 2;

type Positions = Record<string, number>;

const readPositions = (): Positions => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Positions) : {};
  } catch {
    return {};
  }
};

const writePositions = (positions: Positions) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(positions));
  } catch {
    // ignore quota / privacy-mode errors
  }
};

const savePosition = (key: string, y: number) => {
  const positions = readPositions();
  positions[key] = y;
  writePositions(positions);
};

const ScrollToTop: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();
  const key = pathname + search;
  const lastNavAtRef = useRef(0);
  const prevKeyRef = useRef(key);
  // Stamp the navigation timestamp synchronously during render — before the
  // commit-time scroll event from page-shrink clamping fires. If we set this
  // in useEffect, the rAF queued by that scroll event can run first and the
  // gate is wide open.
  if (prevKeyRef.current !== key) {
    prevKeyRef.current = key;
    lastNavAtRef.current = Date.now();
  }

  // Disable browser-native scroll restoration once. We restore manually below
  // so async content load can't clamp the user to the bottom of a short page.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Keep the saved scrollY for the *current URL* fresh while the user reads
  // it. We read `window.location` directly inside `snap` rather than closing
  // over React state: `history.pushState` (which react-router's `navigate`
  // calls) updates `window.location` synchronously, but the new React
  // render that propagates the URL to `useLocation` consumers only runs
  // later. The post-navigation scroll event from layout clamping fires
  // *between* those two points — if we used the React-state key here, we'd
  // overwrite the outgoing page's correct value with the clamped scrollY
  // that belongs to the new page. Pointerdown / keydown fire synchronously
  // before navigation, so they always save under the old URL as intended.
  useEffect(() => {
    let frame = 0;
    const snap = () => {
      const currentKey = window.location.pathname + window.location.search;
      savePosition(currentKey, window.scrollY);
    };
    // Scroll-event snap: ignored briefly after a route change, because the
    // browser clamps scrollY when the destination page is initially shorter
    // and that synthetic scroll would otherwise corrupt the previous page's
    // saved position.
    const snapFromScroll = () => {
      if (Date.now() - lastNavAtRef.current < NAV_QUIET_MS) return;
      snap();
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(snapFromScroll);
    };
    // Pointerdown / keydown fire synchronously *before* the navigation a
    // click triggers, so they always capture the outgoing page's real
    // scrollY. No nav-quiet gate needed.
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("pointerdown", snap, { capture: true });
    document.addEventListener("keydown", snap, { capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("pointerdown", snap, { capture: true });
      document.removeEventListener("keydown", snap, { capture: true });
      cancelAnimationFrame(frame);
    };
  }, []);

  // React to navigation.
  useEffect(() => {
    if (hash) return;
    if (navigationType === "POP") {
      const positions = readPositions();
      const targetY = positions[key];
      // Nothing meaningful to restore. react-router also reports POP on the
      // initial load of a tab, and a saved Y of 0 is just "the top" — in both
      // cases go to the top and DON'T arm the restore watcher. Arming it on a
      // fresh visit (with a stale saved Y from earlier in the session) is what
      // made the page fight the user's scroll on every visit (1NOW-1087).
      if (typeof targetY !== "number" || targetY <= 0) {
        window.scrollTo(0, 0);
        return;
      }

      let cancelled = false;
      let observer: ResizeObserver | null = null;
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      // The Y we last set ourselves. The synthetic "scroll" event our own
      // scrollTo fires reads back ≈ this value, so we can tell our own moves
      // apart from a genuine user scroll. Start at targetY (where we intend to
      // be) so the direction guard is armed even if we never need to scroll.
      let lastAssertedY = targetY;
      let lastHeight = document.documentElement.scrollHeight;
      let convergedTicks = 0;

      const stop = () => {
        if (cancelled) return;
        cancelled = true;
        if (observer) observer.disconnect();
        if (intervalId !== null) clearInterval(intervalId);
        if (timeoutId !== null) clearTimeout(timeoutId);
        window.removeEventListener("wheel", onUserScroll, { capture: true });
        window.removeEventListener("touchmove", onUserScroll, { capture: true });
        window.removeEventListener("keydown", onUserKey, { capture: true });
        window.removeEventListener("scroll", onScrollGuard);
      };

      // A real wheel / touch-drag / scroll-key is unambiguous user intent, and
      // a browser clamp never fires these — so the instant the user tries to
      // scroll we hand control back for the rest of this navigation. Capture
      // phase so we see the intent before our own re-assert can race it;
      // touchmove (not touchstart) so a tap that doesn't move doesn't cancel,
      // but an inertial / momentum swipe does.
      const onUserScroll = () => stop();
      const onUserKey = (e: KeyboardEvent) => {
        // Only treat actual scroll-trigger keys as "user wants to take
        // over", not modifiers / typing.
        const scrollKeys = [
          "PageDown",
          "PageUp",
          "End",
          "Home",
          "ArrowDown",
          "ArrowUp",
          " ",
        ];
        if (scrollKeys.includes(e.key)) stop();
      };

      // Secondary guard for scroll inputs that don't emit wheel/touch/key
      // (e.g. dragging the scrollbar thumb): if the viewport moved UP relative
      // to the position we last asserted, it's the user. Crucially we must NOT
      // mistake a downward browser *clamp* (the document briefly shrank) for a
      // user scroll-up — a clamp lands exactly at maxY, whereas a user scroll
      // lands somewhere the page can still hold (scrollY < maxY). The maxY
      // check separates the two and keeps the ONENOW-469 shrink→grow restore
      // working.
      const onScrollGuard = () => {
        if (cancelled) return;
        const maxY = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        if (
          window.scrollY < lastAssertedY - RESTORE_THRESHOLD_PX &&
          window.scrollY < maxY - RESTORE_THRESHOLD_PX
        ) {
          stop();
        }
      };

      const tryRestore = () => {
        if (cancelled) return;
        const maxY = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        // If the page hasn't grown enough yet to reach the saved position,
        // park the user at the TOP rather than clamping to maxY. Clamping
        // to maxY lands them at the footer of whatever skeleton content
        // happens to be rendered (e.g., a spinner-only early-return on
        // the fleet detail page while `selectedCar` rehydrates) — that's
        // the exact symptom Faiza reproduced on the ONENOW-469 second
        // reopen. We'll re-attempt as content grows; worst case the user
        // sees the top of the page briefly before jumping to position.
        if (maxY < targetY) {
          lastAssertedY = 0;
          window.scrollTo(0, 0);
          convergedTicks = 0;
          return;
        }
        // Genuine displacement from targetY — a post-paint / shrink clamp
        // pulled us off position while the page was settling. Correct it.
        // A user scroll-UP can't reach this branch: onUserScroll /
        // onScrollGuard call stop() first. This displacement gate is the fix
        // the original author flagged as needed (without it the re-assert
        // fought every user scroll — 1NOW-1087).
        if (Math.abs(window.scrollY - targetY) > RESTORE_THRESHOLD_PX) {
          lastAssertedY = targetY;
          window.scrollTo(0, targetY);
          convergedTicks = 0;
          return;
        }
        // Parked at targetY. Once the document height also stops changing the
        // page has settled — stop the watcher early instead of idling (and
        // re-asserting) for the full RESTORE_TIMEOUT_MS. This is what removes
        // the "takes ~1 minute to normalize" feel.
        const height = document.documentElement.scrollHeight;
        if (height === lastHeight) {
          if (++convergedTicks >= RESTORE_STABLE_TICKS) stop();
        } else {
          lastHeight = height;
          convergedTicks = 0;
        }
      };

      // First attempt right after commit.
      tryRestore();

      if (!cancelled) {
        // Re-attempt every time the document grows. The element itself
        // (`<html>`) doesn't change its own height directly — its content
        // does — so observe `<body>` whose `scrollHeight` mirrors the
        // document's effective scroll size.
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(tryRestore);
          observer.observe(document.body);
        }
        // Backstop poll in case ResizeObserver doesn't catch a growth
        // (e.g., absolutely-positioned content with `bottom: …` that
        // expands the scroll area without resizing `<body>`); also drives
        // the convergence-based early stop.
        intervalId = setInterval(tryRestore, RESTORE_TICK_MS);
        timeoutId = setTimeout(stop, RESTORE_TIMEOUT_MS);
        window.addEventListener("wheel", onUserScroll, {
          passive: true,
          capture: true,
        });
        window.addEventListener("touchmove", onUserScroll, {
          passive: true,
          capture: true,
        });
        window.addEventListener("keydown", onUserKey, { capture: true });
        window.addEventListener("scroll", onScrollGuard, { passive: true });
      }

      return stop;
    }

    if (navigationType === "REPLACE") {
      return;
    }

    // PUSH
    window.scrollTo(0, 0);
  }, [key, navigationType]);

  return null;
};

export default ScrollToTop;
